import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format, parseISO } from 'date-fns';
import './css/portal.css';
//import logo from './images/dys-logo-small.png';
import logo from './images/pngs/DYS-Logo-White-Backer.png';

// Private, read-only page for a booked client: /portal/<code>. Shows the event details,
// payments and contract. The code is their key (no login), so it is only ever sent to our
// own API (see PortalController.cs).

const money = (n) =>
    n == null ? '' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

// Gig times are the event's local time with no time zone, so parseISO shows them as typed
const formatDay = (iso) => (iso ? format(parseISO(iso), 'EEEE, MMMM d, yyyy') : '');
const formatTime = (iso) => (iso ? format(parseISO(iso), 'h:mm a') : '');

const ClientPortal = () => {
    const { token } = useParams();
    const api = `/api/portal/${encodeURIComponent(token)}`;

    const [event, setEvent] = useState(null);
    const [loadError, setLoadError] = useState('');

    useEffect(() => {
        fetch(api, { cache: 'no-store' })
            .then(async (res) => {
                if (!res.ok) throw new Error(res.status === 404 ? await res.text() : 'Something went wrong loading your event.');
                return res.json();
            })
            .then(setEvent)
            .catch((err) => setLoadError(err.message || 'Something went wrong loading your event.'));
    }, [api]);

    const head = (
        <Helmet>
            <title>Your Event | Dirty Yachtin' Scoundrels</title>
            <meta name="robots" content="noindex, nofollow" />
            <meta name="referrer" content="no-referrer" />
        </Helmet>
    );

    if (loadError) {
        return (
            <div className="portal">
                {head}
                <header className="portal-header"><img src={logo} alt="Dirty Yachtin' Scoundrels" /></header>
                <section className="portal-card portal-center">
                    <h1>We couldn't open this page</h1>
                    <p>{loadError}</p>
                    <p>Questions? <a href="/Contact">Contact the band</a>.</p>
                </section>
            </div>
        );
    }

    if (!event) {
        return (
            <div className="portal">
                {head}
                <p className="portal-center portal-loading">Loading your event…</p>
            </div>
        );
    }

    return (
        <div className="portal">
            {head}

            <header className="portal-header">
                <img src={logo} alt="Dirty Yachtin' Scoundrels" />
            </header>

            <section className="portal-hero">
                <p className="portal-eyebrow">{event.clientName ? `Ahoy, ${event.clientName}!` : 'Ahoy!'}</p>
                <h1>{event.title || 'Your event'}</h1>
                <p className="portal-when">
                    {formatDay(event.startDateTime)}
                    <br />
                    {formatTime(event.startDateTime)}
                    {event.endDateTime && ` – ${formatTime(event.endDateTime)}`}
                </p>
                {(event.venueName || event.venueAddress) && (
                    <p className="portal-where">
                        {event.venueName && <strong>{event.venueName}</strong>}
                        {event.venueAddress && (
                            <>
                                <br />
                                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venueAddress)}`}
                                    target="_blank" rel="noopener noreferrer">{event.venueAddress}</a>
                            </>
                        )}
                    </p>
                )}
            </section>

            <div className="portal-grid">
                <section className="portal-card">
                    <h2>Payments</h2>
                    <dl className="portal-money">
                        {event.payAmount != null && (<><dt>Band fee</dt><dd>{money(event.payAmount)}</dd></>)}
                        {event.depositAmount != null && (
                            <>
                                <dt>Deposit</dt>
                                <dd>
                                    {money(event.depositAmount)}{' '}
                                    {event.depositPaid
                                        ? <span className="portal-badge ok">Paid{event.depositPaidDate && ` ${format(parseISO(event.depositPaidDate), 'M/d/yy')}`}</span>
                                        : <span className="portal-badge due">Due</span>}
                                </dd>
                            </>
                        )}
                        <dt>Balance</dt>
                        <dd>
                            {event.fullyPaid
                                ? <span className="portal-badge ok">Paid in full</span>
                                : money(event.balanceDue)}
                        </dd>
                    </dl>
                </section>

                <section className="portal-card">
                    <h2>Contract</h2>
                    {event.hasContract ? (
                        <>
                            <p>Your signed agreement, any time you need it.</p>
                            <a className="portal-button" href={`${api}/contract`} target="_blank" rel="noopener noreferrer">
                                View contract (PDF)
                            </a>
                        </>
                    ) : (
                        <p className="portal-muted">Your contract will show up here once it's ready.</p>
                    )}
                </section>
            </div>

            <footer className="portal-footer">
                Questions? <a href="/Contact">Contact the band</a>. Please keep this link private: anyone with it can see this page.
            </footer>
        </div>
    );
};

export default ClientPortal;
